import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { classesApi, feesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'
import { formatAcademicYear, getAcademicYearOptions, getCurrentAcademicYear } from '../utils/academicYear.js'
import {
  formatCurrency,
  formatDate,
  getFeePaymentModeLabel,
  isManualPaymentReferenceRequired,
} from '../utils/fee.js'

const PAYMENT_MODES = ['cash', 'cheque', 'bank_transfer', 'upi']
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
]
const DELIVERY_OPTIONS = [
  { value: 'both', label: 'Both', desc: 'Show in app and send push notification.' },
  { value: 'in_app', label: 'In-app only', desc: 'Visible in notification center only.' },
  { value: 'push', label: 'Push only', desc: 'Send a push notification only.' },
]
const STATUS_ORDER = { overdue: 0, partial: 1, pending: 2, paid: 3 }
const statusTone = {
  paid: 'border-green-100 bg-green-50 text-green-700',
  partial: 'border-amber-100 bg-amber-50 text-amber-700',
  pending: 'border-red-100 bg-red-50 text-red-600',
  overdue: 'border-red-200 bg-red-100 text-red-700',
}

const getNumber = (value) => Number(value || 0)
const getEntityId = (value) => (typeof value === 'string' ? value : value?._id || value?.id || '')
const classLabel = (item) => `${item?.name || 'Class'}${item?.section ? ` - ${item.section}` : ''}`
const compactClassLabel = (value) => String(value || '').replace(/^class\s+/i, '').replace(/\s*-\s*/g, '-').trim()
const displayClassLabel = (value) => {
  const label = String(value || '').trim()
  if (!label) return ''
  return /^class\b/i.test(label) ? label : `Class ${label}`
}
const studentName = (student) =>
  [student?.firstName, student?.lastName].filter(Boolean).join(' ').trim() || student?.name || 'Unknown Student'
const feePlanName = (account, groupClassLabel) => {
  const raw = String(account?.structureId?.name || account?.structure?.name || account?.feeStructureId?.name || '').trim()
  const classMatch = raw.match(/^fee\s*structure\s*for\s*class\s+(.+)$/i)
  if (classMatch?.[1]) return `${displayClassLabel(classMatch[1].trim())} Fee Plan`
  if (/^fee\s*structure$/i.test(raw) && groupClassLabel) return `${displayClassLabel(groupClassLabel)} Fee Plan`
  return raw || (groupClassLabel ? `${displayClassLabel(groupClassLabel)} Fee Plan` : 'Fee Plan')
}
const netTotal = (account) =>
  Math.max(
    0,
    getNumber(account?.totalAmount) -
      getNumber(account?.discountAmount) -
      getNumber(account?.concessionAmount) +
      getNumber(account?.fineAmount)
  )
const combinedStatus = (items) => {
  const hasOutstanding = items.some((item) => getNumber(item?.outstanding) > 0)
  const hasOverdue = items.some((item) => String(item?.status || '').toLowerCase() === 'overdue' && getNumber(item?.outstanding) > 0)
  const hasPaid = items.some((item) => getNumber(item?.paidAmount) > 0)
  if (hasOverdue) return 'overdue'
  if (!hasOutstanding) return 'paid'
  if (hasPaid) return 'partial'
  return 'pending'
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function groupAccounts(rawAccounts) {
  const groups = new Map()
  rawAccounts.forEach((account) => {
    const student = account?.studentId
    const studentId = getEntityId(student) || getEntityId(account) || 'unknown'
    const academicYear = formatAcademicYear(account?.academicYear)
    const groupId = `${studentId}::${academicYear || 'no-year'}`
    const current = groups.get(groupId) || {
      _groupId: groupId,
      student,
      studentId,
      studentName: studentName(student),
      admissionNo: student?.admissionNo || '',
      classLabel: compactClassLabel(classLabel(account?.classId)),
      academicYear,
      totalAmount: 0,
      paidAmount: 0,
      outstanding: 0,
      discountAmount: 0,
      concessionAmount: 0,
      fineAmount: 0,
      accounts: [],
    }

    current.totalAmount += getNumber(account?.totalAmount)
    current.paidAmount += getNumber(account?.paidAmount)
    current.outstanding += getNumber(account?.outstanding)
    current.discountAmount += getNumber(account?.discountAmount)
    current.concessionAmount += getNumber(account?.concessionAmount)
    current.fineAmount += getNumber(account?.fineAmount)
    current.accounts.push(account)
    groups.set(groupId, current)
  })

  return [...groups.values()]
    .map((group) => {
      const sortedAccounts = [...group.accounts].sort((left, right) => {
        const leftStatus = String(left?.status || 'pending').toLowerCase()
        const rightStatus = String(right?.status || 'pending').toLowerCase()
        const statusDiff = (STATUS_ORDER[leftStatus] ?? 99) - (STATUS_ORDER[rightStatus] ?? 99)
        if (statusDiff !== 0) return statusDiff
        const outstandingDiff = getNumber(right?.outstanding) - getNumber(left?.outstanding)
        if (outstandingDiff !== 0) return outstandingDiff
        return String(left?.structureId?.name || '').localeCompare(String(right?.structureId?.name || ''), 'en', { sensitivity: 'base' })
      })
      const groupNetTotal = Math.max(0, group.totalAmount - group.discountAmount - group.concessionAmount + group.fineAmount)
      return {
        ...group,
        accounts: sortedAccounts,
        netTotal: groupNetTotal,
        paidPct: groupNetTotal > 0 ? Math.min(100, Math.round((group.paidAmount / groupNetTotal) * 100)) : 0,
        status: combinedStatus(sortedAccounts),
        overdueHeadCount: sortedAccounts.filter((item) => String(item?.status || '').toLowerCase() === 'overdue' && getNumber(item?.outstanding) > 0).length,
      }
    })
    .sort((left, right) => {
      const statusDiff = (STATUS_ORDER[left.status] ?? 99) - (STATUS_ORDER[right.status] ?? 99)
      if (statusDiff !== 0) return statusDiff
      const outstandingDiff = getNumber(right.outstanding) - getNumber(left.outstanding)
      if (outstandingDiff !== 0) return outstandingDiff
      return String(left.studentName).localeCompare(String(right.studentName), 'en', { sensitivity: 'base' })
    })
}

function groupDefaulters(rawDefaulters) {
  const groups = new Map()
  rawDefaulters.forEach((entry) => {
    const student = entry?.student || entry?.studentId
    const studentId = getEntityId(student) || getEntityId(entry) || 'unknown'
    const academicYear = formatAcademicYear(entry?.academicYear)
    const groupId = `${studentId}::${academicYear || 'no-year'}`
    const current = groups.get(groupId) || {
      _groupId: groupId,
      student,
      studentId,
      studentName: studentName(student),
      admissionNo: student?.admissionNo || '',
      classLabel: compactClassLabel(classLabel(entry?.classId)),
      academicYear,
      totalAmount: 0,
      paidAmount: 0,
      outstanding: 0,
      overdueAmount: 0,
      overdueCount: 0,
      accounts: [],
    }

    current.totalAmount += getNumber(entry?.totalAmount)
    current.paidAmount += getNumber(entry?.paidAmount)
    current.outstanding += getNumber(entry?.outstanding ?? entry?.overdueAmount)
    current.overdueAmount += getNumber(entry?.overdueAmount ?? entry?.outstanding)
    current.overdueCount += getNumber(entry?.overdueCount)
    current.accounts.push(entry)
    groups.set(groupId, current)
  })

  return [...groups.values()]
    .map((group) => ({
      ...group,
      paidPct: group.totalAmount > 0 ? Math.min(100, Math.round((group.paidAmount / group.totalAmount) * 100)) : 0,
      accounts: [...group.accounts].sort((left, right) => {
        const overdueDiff = getNumber(right?.overdueAmount ?? right?.outstanding) - getNumber(left?.overdueAmount ?? left?.outstanding)
        if (overdueDiff !== 0) return overdueDiff
        return feePlanName(left, group.classLabel).localeCompare(feePlanName(right, group.classLabel), 'en', { sensitivity: 'base' })
      }),
    }))
    .sort((left, right) => {
      const overdueDiff = getNumber(right.overdueAmount) - getNumber(left.overdueAmount)
      if (overdueDiff !== 0) return overdueDiff
      return String(left.studentName).localeCompare(String(right.studentName), 'en', { sensitivity: 'base' })
    })
}

export default function FeeCollectionPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentAcademicYear = getCurrentAcademicYear()
  const [tab, setTab] = useState('accounts')
  const [accounts, setAccounts] = useState([])
  const [defaulters, setDefaulters] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [filterAcademicYear, setFilterAcademicYear] = useState(currentAcademicYear)
  const [filterStatus, setFilterStatus] = useState('')
  const [expandedGroups, setExpandedGroups] = useState({})
  const [payModal, setPayModal] = useState(null)
  const [payForm, setPayForm] = useState({
    amount: '',
    paymentMode: 'cash',
    paymentDate: new Date().toISOString().slice(0, 10),
    referenceNumber: '',
    notes: '',
  })
  const [paying, setPaying] = useState(false)
  const [successData, setSuccessData] = useState(null)
  const [reminderModal, setReminderModal] = useState(null)
  const [reminderForm, setReminderForm] = useState({
    target: 'all',
    studentId: '',
    studentName: '',
    academicYear: currentAcademicYear,
    title: 'Fee Payment Reminder',
    message: '',
    delivery: 'both',
  })
  const [sendingReminder, setSendingReminder] = useState(false)
  const [exportModal, setExportModal] = useState(false)
  const [exportStep, setExportStep] = useState(1)
  const [exportScope, setExportScope] = useState('filtered')
  const [exportFormat, setExportFormat] = useState('csv')
  const [exporting, setExporting] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const accountParams = {}
      const defaulterParams = {}
      if (filterClass) {
        accountParams.classId = filterClass
        defaulterParams.classId = filterClass
      }
      if (filterAcademicYear) {
        accountParams.academicYear = filterAcademicYear
        defaulterParams.academicYear = filterAcademicYear
      }

      const [accountsRes, defaultersRes, classesRes] = await Promise.all([
        feesApi.getAccounts(accountParams),
        feesApi.getDefaulters(defaulterParams),
        classesApi.getAll(),
      ])

      setAccounts(Array.isArray(accountsRes) ? accountsRes : [])
      setDefaulters(Array.isArray(defaultersRes) ? defaultersRes : [])
      setClasses(Array.isArray(classesRes) ? classesRes : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load fee collection data')
      setAccounts([])
      setDefaulters([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filterClass, filterAcademicYear])

  const academicYearOptions = useMemo(() => {
    const values = [
      ...getAcademicYearOptions({ pastCount: 2, futureCount: 1 }),
      currentAcademicYear,
      ...accounts.map((item) => formatAcademicYear(item?.academicYear)).filter(Boolean),
      ...defaulters.map((item) => formatAcademicYear(item?.academicYear)).filter(Boolean),
    ]
    return [...new Set(values)].sort((left, right) => right.localeCompare(left, 'en', { numeric: true }))
  }, [accounts, currentAcademicYear, defaulters])

  const groupedAccounts = useMemo(() => groupAccounts(accounts), [accounts])
  const groupedDefaulters = useMemo(() => groupDefaulters(defaulters), [defaulters])

  const filteredAccounts = useMemo(() => {
    let list = groupedAccounts
    if (filterStatus) list = list.filter((item) => item.status === filterStatus)
    if (!search.trim()) return list
    const query = search.trim().toLowerCase()
    return list.filter((item) =>
      String(item.studentName || '').toLowerCase().includes(query) ||
      String(item.admissionNo || '').toLowerCase().includes(query) ||
      String(item.classLabel || '').toLowerCase().includes(query) ||
      String(item.academicYear || '').toLowerCase().includes(query) ||
      item.accounts.some((account) => String(account?.structureId?.name || '').toLowerCase().includes(query))
    )
  }, [filterStatus, groupedAccounts, search])

  const filteredDefaulters = useMemo(() => {
    if (!search.trim()) return groupedDefaulters
    const query = search.trim().toLowerCase()
    return groupedDefaulters.filter((item) =>
      String(item.studentName || '').toLowerCase().includes(query) ||
      String(item.admissionNo || '').toLowerCase().includes(query) ||
      String(item.classLabel || '').toLowerCase().includes(query) ||
      String(item.academicYear || '').toLowerCase().includes(query) ||
      item.accounts.some((account) => feePlanName(account, item.classLabel).toLowerCase().includes(query))
    )
  }, [groupedDefaulters, search])

  const data = tab === 'accounts' ? filteredAccounts : filteredDefaulters
  const selectedClass = filterClass ? classes.find((item) => (item._id || item.id) === filterClass) : null
  const activeFilterCount = [
    filterAcademicYear && filterAcademicYear !== currentAcademicYear,
    Boolean(filterClass),
    tab === 'accounts' && Boolean(filterStatus),
  ].filter(Boolean).length

  useEffect(() => {
    const targetAccountId = searchParams.get('accountId')
    if (!targetAccountId || !accounts.length || payModal) return
    const targetAccount = accounts.find((item) => (item._id || item.id) === targetAccountId)
    if (!targetAccount) return
    openPay(targetAccount)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('accountId')
    setSearchParams(nextParams)
  }, [accounts, payModal, searchParams, setSearchParams])

  const resetFilters = () => {
    setFilterAcademicYear(currentAcademicYear)
    setFilterClass('')
    setFilterStatus('')
  }

  const toggleGroup = (groupId) =>
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }))

  const openPay = (account) => {
    setPayModal(account)
    setSuccessData(null)
    setPayForm({
      amount: String(account.outstanding || ''),
      paymentMode: 'cash',
      paymentDate: new Date().toISOString().slice(0, 10),
      referenceNumber: '',
      notes: '',
    })
  }

  const closePay = () => {
    setPayModal(null)
    setSuccessData(null)
    setPaying(false)
  }

  const handlePay = async () => {
    const amount = Number(payForm.amount)
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount')
      return
    }
    if (isManualPaymentReferenceRequired(payForm.paymentMode) && !payForm.referenceNumber.trim()) {
      toast.error(`Enter a reference number for ${getFeePaymentModeLabel(payForm.paymentMode)} payments`)
      return
    }

    try {
      setPaying(true)
      const accountId = payModal._id || payModal.id
      const result = await feesApi.recordPayment(accountId, {
        amount,
        paymentMode: payForm.paymentMode,
        paymentDate: payForm.paymentDate,
        referenceNumber: payForm.referenceNumber.trim() || undefined,
        notes: payForm.notes.trim() || undefined,
      })
      setSuccessData({
        paymentId: result?.payment?._id || result?.paymentId || null,
        receiptNumber: result?.receiptNumber || result?.payment?.receiptNumber || 'Recorded',
      })
      toast.success('Payment recorded')
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed')
    } finally {
      setPaying(false)
    }
  }

  const openReminderAll = () => {
    setReminderForm({
      target: 'all',
      studentId: '',
      studentName: '',
      academicYear: filterAcademicYear,
      title: 'Fee Payment Reminder',
      message: 'Dear Parent/Student,\n\nThis is a reminder that your fee payment is pending. Please clear your dues at the earliest to avoid any inconvenience.\n\nThank you.',
      delivery: 'both',
    })
    setReminderModal(true)
  }

  const openReminderSingle = (item) => {
    const student = item.student || item.studentId
    const name = studentName(student) || item.studentName || 'Student'
    const outstanding = getNumber(item.overdueAmount || item.outstanding)
    const classYear = [item.classLabel || classLabel(item.classId), formatAcademicYear(item.academicYear || filterAcademicYear || '')]
      .filter(Boolean)
      .join(' · ')

    setReminderForm({
      target: 'single',
      studentId: getEntityId(student || item.studentId),
      studentName: name,
      academicYear: formatAcademicYear(item.academicYear || filterAcademicYear || ''),
      title: 'Fee Payment Reminder',
      message: `Dear ${name},\n\nYour fee of ${formatCurrency(outstanding)}${classYear ? ` for ${classYear}` : ''} is pending. Please make the payment at the earliest.\n\nThank you.`,
      delivery: 'both',
    })
    setReminderModal(true)
  }

  const handleSendReminder = async () => {
    if (!reminderForm.title.trim() || !reminderForm.message.trim()) {
      toast.error('Title and message are required')
      return
    }

    try {
      setSendingReminder(true)
      const body = {
        title: reminderForm.title.trim(),
        message: reminderForm.message.trim(),
        delivery: reminderForm.delivery,
      }

      if (reminderForm.target === 'single') {
        if (!reminderForm.studentId) {
          toast.error('No student selected')
          return
        }
        body.studentId = reminderForm.studentId
      } else {
        const studentIds = [...new Set(data.map((item) => getEntityId(item.studentId || item.student || item)).filter(Boolean))]
        if (!studentIds.length) {
          toast.error('No students found for this reminder')
          return
        }
        body.studentIds = studentIds
        if (filterClass) body.classId = filterClass
      }
      if (reminderForm.academicYear) body.academicYear = reminderForm.academicYear

      const res = await feesApi.sendReminders(body)
      toast.success(`Reminder sent to ${res?.sent ?? (reminderForm.target === 'single' ? 1 : 'all')} recipient(s)`)
      setReminderModal(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminder')
    } finally {
      setSendingReminder(false)
    }
  }

  const openExportModal = () => {
    setExportStep(1)
    setExportScope('filtered')
    setExportFormat('csv')
    setExportModal(true)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const params = { format: exportFormat }
      if (exportScope === 'filtered') {
        if (filterClass) params.classId = filterClass
        if (filterAcademicYear) params.academicYear = filterAcademicYear
        if (search.trim()) params.search = search.trim()
        if (tab === 'accounts' && filterStatus) params.status = filterStatus
      }

      const response = tab === 'accounts'
        ? await feesApi.exportAccounts(params, exportFormat)
        : await feesApi.exportDefaulters(params, exportFormat)

      const filename = [
        `fee-${tab}`,
        exportScope === 'filtered' ? filterAcademicYear || 'all-years' : 'all-records',
        filterClass && selectedClass ? compactClassLabel(classLabel(selectedClass)) : '',
        tab === 'accounts' ? filterStatus || '' : '',
      ].filter(Boolean).join('-')

      if (exportFormat === 'pdf') {
        const { downloadAsPdf } = await import('../utils/downloadPdf.js')
        await downloadAsPdf(response.data, filename, { orientation: 'landscape' })
        toast.success('PDF downloaded')
      } else {
        downloadBlob(new Blob([response.data], { type: 'text/csv' }), `${filename}.csv`)
        toast.success('CSV downloaded')
      }

      setExportModal(false)
    } catch {
      toast.error('Export failed — try again')
    } finally {
      setExporting(false)
    }
  }

  const renderAccountGroup = (item) => {
    const isExpanded = Boolean(expandedGroups[item._groupId]) || item.accounts.length === 1
    const status = item.status || 'pending'
    return (
      <div key={item._groupId} className="card overflow-hidden">
        <div className="p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <Avatar src={item.student?.avatar} name={item.studentName} size="md" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-lg font-semibold text-gray-900">{item.studentName}</p>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold uppercase ${statusTone[status] || statusTone.pending}`}>
                    {status}
                  </span>
                  {item.overdueHeadCount > 0 && (
                    <span className="rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">
                      {item.overdueHeadCount} overdue
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {item.admissionNo ? `Adm# ${item.admissionNo} • ` : ''}
                  {item.classLabel ? `${displayClassLabel(item.classLabel)} • ` : ''}
                  {item.academicYear || 'No academic year'}
                </p>
                <p className="mt-0.5 text-sm text-gray-400">
                  {item.accounts.length > 1 ? `${item.accounts.length} fee plans combined` : feePlanName(item.accounts[0], item.classLabel)}
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div className={`h-full rounded-full ${status === 'paid' ? 'bg-green-500' : status === 'overdue' ? 'bg-red-500' : status === 'partial' ? 'bg-amber-500' : 'bg-slate-400'}`} style={{ width: `${item.paidPct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-500">{item.paidPct}% paid</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 xl:items-end">
              <div className="text-left xl:text-right">
                <p className="text-sm text-gray-500">Amount Pending</p>
                <p className={`mt-1 text-xl font-bold ${status === 'paid' ? 'text-green-700' : 'text-red-600'}`}>
                  {formatCurrency(item.outstanding)}
                </p>
                <p className="text-xs text-gray-400">
                  {formatCurrency(item.paidAmount)} collected / {formatCurrency(item.netTotal || item.totalAmount)}
                </p>
              </div>
              {item.accounts.length > 1 && (
                <button onClick={() => toggleGroup(item._groupId)} className="btn-secondary min-h-[36px] px-4 py-1.5 text-xs">
                  {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  {isExpanded ? 'Hide Fee Plans' : 'View Fee Plans'}
                </button>
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="space-y-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
            {item.accounts.map((account, index) => {
              const accountId = account._id || account.id
              const accountStatus = String(account.status || 'pending').toLowerCase()
              const due = getNumber(account.outstanding)
              const accountNet = netTotal(account)
              const accountPct = accountNet > 0 ? Math.min(100, Math.round((getNumber(account.paidAmount) / accountNet) * 100)) : 0
              return (
                <div key={accountId || index} className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{feePlanName(account, item.classLabel)}</p>
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${statusTone[accountStatus] || statusTone.pending}`}>
                          {accountStatus}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">{classLabel(account.classId)}{account.academicYear ? ` • ${account.academicYear}` : ''}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
                          <div className={`h-full rounded-full ${accountStatus === 'paid' ? 'bg-green-500' : accountStatus === 'overdue' ? 'bg-red-500' : accountStatus === 'partial' ? 'bg-amber-500' : 'bg-slate-400'}`} style={{ width: `${accountPct}%` }} />
                        </div>
                        <span className="text-xs text-gray-400">{accountPct}%</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:items-end">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(account.paidAmount)}
                        <span className="text-xs font-normal text-gray-400"> / {formatCurrency(accountNet || account.totalAmount)}</span>
                      </p>
                      {due > 0 && <p className="text-xs font-semibold text-red-600">Due {formatCurrency(due)}</p>}
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => navigate(`/fees/accounts/${accountId}`)} className="btn-secondary min-h-[32px] px-3 py-1 text-xs">Details</button>
                        {due > 0 && (
                          <button onClick={() => openPay(account)} className="btn-primary min-h-[32px] px-3 py-1 text-xs">
                            <CreditCard className="h-3.5 w-3.5" /> Collect
                          </button>
                        )}
                        {due > 0 && (
                          <button onClick={() => openReminderSingle(account)} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100">
                            <Bell className="inline h-3.5 w-3.5" /> Remind
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const renderDefaulterGroup = (item) => (
    <div key={item._groupId} className="card p-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <Avatar src={item.student?.avatar} name={item.studentName} size="md" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-semibold text-gray-900">{item.studentName}</p>
            <p className="mt-1 text-sm text-gray-500">
              {item.admissionNo ? `Adm# ${item.admissionNo} • ` : ''}
              {item.classLabel ? `${displayClassLabel(item.classLabel)} • ` : ''}
              {item.academicYear || 'No academic year'}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-red-50">
                <div className="h-full rounded-full bg-red-500" style={{ width: `${item.paidPct}%` }} />
              </div>
              <span className="text-xs font-semibold text-gray-500">{item.paidPct}% paid</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.accounts.slice(0, 2).map((account, index) => (
                <span key={account._id || account.id || index} className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600">
                  {feePlanName(account, item.classLabel)}
                </span>
              ))}
              {item.accounts.length > 2 && (
                <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600">
                  +{item.accounts.length - 2} more
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 xl:items-end">
          <div className="text-left xl:text-right">
            <p className="text-sm text-gray-500">Overdue Amount</p>
            <p className="mt-1 text-xl font-bold text-red-600">{formatCurrency(item.overdueAmount || item.outstanding)}</p>
            <p className="text-xs text-gray-400">{item.overdueCount || 0} overdue installment{item.overdueCount === 1 ? '' : 's'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => openReminderSingle(item)} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100">
              <Bell className="inline h-3.5 w-3.5" /> Remind
            </button>
            <button onClick={() => navigate(`/fees/accounts/${item.accounts[0]?._id || item.accounts[0]?.id || item._groupId}`)} className="btn-secondary min-h-[36px] px-3 py-1.5 text-xs">
              Open Account
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Collection"
        subtitle="Same admin collection flow as Edubridge RN: students and defaulters in one place, with exports and reminders."
        chips={[
          loading ? 'Loading fee data' : `${filteredAccounts.length} students`,
          loading ? 'Loading defaulters' : `${filteredDefaulters.length} defaulters`,
          filterAcademicYear || 'All academic years',
        ]}
        actions={
          <>
            <button onClick={openExportModal} className="btn-secondary">
              <Download className="h-4 w-4" /> Export
            </button>
            <button onClick={fetchData} className="btn-secondary">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </>
        }
      />

      <div className="card p-2">
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'accounts', label: 'Students' },
            { value: 'defaulters', label: 'Defaulters' },
          ].map((item) => (
            <button
              key={item.value}
              onClick={() => setTab(item.value)}
              className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === item.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card p-4 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, admission no, class, year, or fee plan..."
              className="w-full bg-transparent text-sm outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <select className="input-field w-full sm:w-auto" value={filterAcademicYear} onChange={(event) => setFilterAcademicYear(event.target.value)}>
            <option value="">All Years</option>
            {academicYearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
          <select className="input-field w-full sm:w-auto" value={filterClass} onChange={(event) => setFilterClass(event.target.value)}>
            <option value="">All Classes</option>
            {classes.map((item) => <option key={item._id || item.id} value={item._id || item.id}>{classLabel(item)}</option>)}
          </select>
          {tab === 'accounts' && (
            <select className="input-field w-full sm:w-auto" value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
              {STATUS_OPTIONS.map((item) => <option key={item.value || 'all'} value={item.value}>{item.label}</option>)}
            </select>
          )}
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="btn-secondary">
              <RefreshCw className="h-4 w-4" /> Reset
            </button>
          )}
        </div>
      </div>

      {tab === 'defaulters' && !loading && data.length > 0 && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-amber-900">{data.length} defaulter{data.length === 1 ? '' : 's'}</p>
              <p className="text-sm text-amber-700">
                {[filterClass ? 'Selected class' : 'All classes', filterAcademicYear || 'All years'].join(' · ')}
              </p>
            </div>
            <button onClick={openReminderAll} className="btn-primary">
              <Bell className="h-4 w-4" /> Notify All
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          [...Array(5)].map((_, index) => (
            <div key={index} className="card animate-pulse p-5">
              <div className="h-5 w-1/3 rounded bg-gray-100" />
              <div className="mt-3 h-4 w-2/3 rounded bg-gray-100" />
              <div className="mt-4 h-2 rounded bg-gray-100" />
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="empty-state card">
            <CreditCard className="h-10 w-10 text-gray-200" />
            <p className="empty-state-title">{tab === 'accounts' ? 'No fee accounts found' : 'No defaulters found'}</p>
            <p className="empty-state-copy">Adjust filters or create fee accounts from a fee structure.</p>
          </div>
        ) : (
          data.map((item) => tab === 'accounts' ? renderAccountGroup(item) : renderDefaulterGroup(item))
        )}
      </div>

      {reminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {reminderForm.target === 'single' ? `Remind: ${reminderForm.studentName}` : 'Notify All Defaulters'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {reminderForm.target === 'all' && filterClass ? 'Filtered to selected class only' : reminderForm.academicYear || 'All academic years'}
                </p>
              </div>
              <button onClick={() => setReminderModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[76vh] space-y-4 overflow-y-auto px-6 py-5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">Notification Title</label>
                <input
                  className="input-field w-full"
                  value={reminderForm.title}
                  onChange={(event) => setReminderForm((prev) => ({ ...prev, title: event.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">Message</label>
                <textarea
                  className="input-field h-36 w-full resize-none"
                  value={reminderForm.message}
                  onChange={(event) => setReminderForm((prev) => ({ ...prev, message: event.target.value }))}
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold text-gray-600">Delivery</label>
                <div className="grid gap-3 sm:grid-cols-3">
                  {DELIVERY_OPTIONS.map((item) => (
                    <button
                      key={item.value}
                      onClick={() => setReminderForm((prev) => ({ ...prev, delivery: item.value }))}
                      className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                        reminderForm.delivery === item.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'
                      }`}
                    >
                      <p className={`text-sm font-semibold ${reminderForm.delivery === item.value ? 'text-primary-700' : 'text-gray-800'}`}>{item.label}</p>
                      <p className="mt-1 text-xs text-gray-500">{item.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              <button onClick={() => setReminderModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSendReminder} disabled={sendingReminder} className="btn-primary">
                {sendingReminder ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                {reminderForm.target === 'single' ? 'Send Reminder' : `Notify All (${data.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            {successData ? (
              <div className="p-8 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-gray-900">Payment recorded</h3>
                <p className="mt-2 text-sm text-gray-500">Receipt number</p>
                <p className="mt-1 text-base font-semibold text-primary-700">{successData.receiptNumber}</p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  {successData.paymentId && (
                    <button onClick={() => navigate(`/fees/receipts/${successData.paymentId}`, { state: { accountPath: `/fees/accounts/${payModal._id || payModal.id}` } })} className="btn-primary flex-1">
                      View Receipt
                    </button>
                  )}
                  <button onClick={closePay} className="btn-secondary flex-1">Close</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <Avatar src={payModal.studentId?.avatar} name={studentName(payModal.studentId)} size="sm" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Record Payment</h3>
                      <p className="mt-1 text-sm text-gray-500">{studentName(payModal.studentId)}</p>
                    </div>
                  </div>
                  <button onClick={closePay} className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4 px-6 py-5">
                  <div className="rounded-2xl bg-gray-50 px-4 py-4 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-gray-500">Outstanding balance</span>
                      <span className="font-semibold text-red-600">{formatCurrency(payModal.outstanding || 0)}</span>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">Amount (₹) *</label>
                    <input type="number" className="input-field w-full" value={payForm.amount} onChange={(event) => setPayForm((prev) => ({ ...prev, amount: event.target.value }))} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold text-gray-600">Payment Mode</label>
                    <div className="flex flex-wrap gap-2">
                      {PAYMENT_MODES.map((mode) => (
                        <button
                          key={mode}
                          onClick={() => setPayForm((prev) => ({ ...prev, paymentMode: mode }))}
                          className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                            payForm.paymentMode === mode ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200'
                          }`}
                        >
                          {getFeePaymentModeLabel(mode)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">Payment Date</label>
                    <input type="date" className="input-field w-full" value={payForm.paymentDate} onChange={(event) => setPayForm((prev) => ({ ...prev, paymentDate: event.target.value }))} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">
                      Reference Number {isManualPaymentReferenceRequired(payForm.paymentMode) ? '*' : '(optional)'}
                    </label>
                    <input
                      className="input-field w-full"
                      value={payForm.referenceNumber}
                      onChange={(event) => setPayForm((prev) => ({ ...prev, referenceNumber: event.target.value }))}
                      placeholder={isManualPaymentReferenceRequired(payForm.paymentMode) ? 'Enter UTR / transaction / instrument number' : 'Reference not needed for cash'}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">Notes</label>
                    <textarea className="input-field h-20 w-full resize-none" value={payForm.notes} onChange={(event) => setPayForm((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Optional notes" />
                  </div>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end">
                  <button onClick={closePay} className="btn-secondary">Cancel</button>
                  <button onClick={handlePay} disabled={paying} className="btn-primary">
                    {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    Record Payment
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Export {tab === 'accounts' ? 'Fee Accounts' : 'Defaulters'}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {exportStep === 1 ? 'Step 1 of 2 — Choose records' : 'Step 2 of 2 — Choose format'}
                </p>
              </div>
              <button onClick={() => setExportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {exportStep === 1 ? (
              <div className="space-y-3 px-6 py-5">
                {[
                  { value: 'filtered', label: 'Current Filters', desc: 'Export records matching the filters on this screen' },
                  { value: 'all', label: 'All Records', desc: 'Export every record for this tab' },
                ].map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setExportScope(item.value)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                      exportScope === item.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'
                    }`}
                  >
                    <p className={`text-sm font-semibold ${exportScope === item.value ? 'text-primary-700' : 'text-gray-800'}`}>{item.label}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{item.desc}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-3 px-6 py-5">
                {[
                  { value: 'csv', label: 'Excel / CSV', desc: 'Open in Excel or Google Sheets', icon: FileText },
                  { value: 'pdf', label: 'PDF', desc: 'Print-ready report', icon: Download },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.value}
                      onClick={() => setExportFormat(item.value)}
                      className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                        exportFormat === item.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'
                      }`}
                    >
                      <Icon className={`h-5 w-5 flex-shrink-0 ${exportFormat === item.value ? 'text-primary-600' : 'text-gray-400'}`} />
                      <div>
                        <p className={`text-sm font-semibold ${exportFormat === item.value ? 'text-primary-700' : 'text-gray-800'}`}>{item.label}</p>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}

            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              {exportStep === 1 ? (
                <>
                  <button onClick={() => setExportModal(false)} className="btn-secondary">Cancel</button>
                  <button onClick={() => setExportStep(2)} className="btn-primary">Next</button>
                </>
              ) : (
                <>
                  <button onClick={() => setExportStep(1)} className="btn-secondary">Back</button>
                  <button onClick={handleExport} disabled={exporting} className="btn-primary">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Download
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
